import { DateRangePicker } from '@bjarkehs/react-nice-dates';
import { Button, Group, Stack, Text, TextInput } from '@mantine/core';
import { enAU } from 'date-fns/locale';
import { DateTime } from 'luxon';
import React from 'react';
import { useNiceDatesFocusAdapter } from '../../../hooks/useNiceDatesRecalcOnFocus';

interface ShiftSessionFiltersProps {
  startDate: DateTime;
  endDate: DateTime;
  name: string;
  onNameChange: (name: string) => void;
  onStartDateChange: (date: DateTime) => void;
  onEndDateChange: (date: DateTime) => void;
  onApplyFilters: () => void;
  onResetFilters: () => void;
  isLoading?: boolean;
  paymentStatuses: Array<'unpaid' | 'partial' | 'paid'>;
  onPaymentStatusesChange: (statuses: Array<'unpaid' | 'partial' | 'paid'>) => void;
}

export const ShiftSessionFilters: React.FC<ShiftSessionFiltersProps> = ({
  startDate,
  endDate,
  name,
  onStartDateChange,
  onEndDateChange,
  onNameChange,
  onApplyFilters,
  onResetFilters,
  isLoading = false,
  paymentStatuses,
  onPaymentStatusesChange
}) => {
  const adaptFocus = useNiceDatesFocusAdapter();
  const handleStartDateChange = (date: Date | null) => {
    if (isLoading) {
      return;
    }

    if (date) {
      onStartDateChange(DateTime.fromJSDate(date).startOf('day'));
    }
  };

  const handleEndDateChange = (date: Date | null) => {
    if (isLoading) {
      return;
    }

    if (date) {
      onEndDateChange(DateTime.fromJSDate(date).startOf('day'));
    }
  };

  return (
    <Stack gap="sm">
      <Text size="lg" fw={600}>Filter Sessions</Text>
      <Group grow>
        <Stack gap="xs" style={{ flex: 1 }}>
          <Text size="sm" fw={500}>Shift Name</Text>
          <TextInput
            placeholder="Enter shift name"
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            disabled={isLoading}
          />
        </Stack>
      </Group>

      <Group>
        <Text size="sm" fw={500}>Payment Status</Text>
        <Group gap="xs">
          {(['unpaid', 'partial', 'paid'] as const).map((status) => {
            const selected = paymentStatuses.includes(status);
            return (
              <Button
                key={status}
                size="xs"
                color={selected ? (status === 'paid' ? 'green' : status === 'partial' ? 'yellow' : 'blue') : 'gray'}
                variant={selected ? 'filled' : 'outline'}
                onClick={() => {
                  if (isLoading) return;
                  // Prevent empty selection; always keep at least one status selected
                  const next = selected
                    ? (paymentStatuses.length > 1 ? paymentStatuses.filter(s => s !== status) : paymentStatuses)
                    : [...paymentStatuses, status];
                  onPaymentStatusesChange(next);
                }}
                disabled={isLoading}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </Button>
            );
          })}
        </Group>
      </Group>

      <div className="nice-dates-compact">
        <DateRangePicker
          startDate={startDate.toJSDate()}
          endDate={endDate.toJSDate()}
          onStartDateChange={handleStartDateChange}
          onEndDateChange={handleEndDateChange}
          locale={enAU}
          format="yyyy-MM-dd"
        >
          {({ startDateInputProps, endDateInputProps, focus }) => (
            <>
              <Group mb={4} gap="xs">
                <Button
                  size="xs"
                  variant="light"
                  onClick={() => {
                    const now = DateTime.now();
                    onStartDateChange(now.startOf('month'));
                    onEndDateChange(now);
                  }}
                  disabled={isLoading}
                >This Month</Button>
                <Button
                  size="xs"
                  variant="light"
                  onClick={() => {
                    const now = DateTime.now();
                    onStartDateChange(now.startOf('year'));
                    onEndDateChange(now);
                  }}
                  disabled={isLoading}
                >This Year</Button>
              </Group>
              <Group grow>
                <Stack gap="xs">
                  <Text size="sm" fw={500}>Start Date (YYYY-MM-DD)</Text>
                  <TextInput
                    {...startDateInputProps}
                    type="text"
                    placeholder="YYYY-MM-DD"
                    className={`input${focus === 'startDate' ? ' -focused' : ''}`}
                    disabled={isLoading}
                    onFocus={adaptFocus(startDateInputProps.onFocus)}
                  />
                </Stack>

                <Stack gap="xs">
                  <Text size="sm" fw={500}>End Date (YYYY-MM-DD)</Text>
                  <TextInput
                    {...endDateInputProps}
                    type="text"
                    placeholder="YYYY-MM-DD"
                    className={`input${focus === 'endDate' ? ' -focused' : ''}`}
                    disabled={isLoading}
                    onFocus={adaptFocus(endDateInputProps.onFocus)}
                  />
                </Stack>
              </Group>
            </>
          )}
        </DateRangePicker>
      </div>

      <Group>
        <Button
          onClick={onApplyFilters}
          loading={isLoading}
          disabled={!startDate.isValid || !endDate.isValid || startDate > endDate}
        >
          Apply Filters
        </Button>
        <Button
          variant="outline"
          onClick={onResetFilters}
          disabled={isLoading}
        >
          Reset
        </Button>
      </Group>
    </Stack>
  );
};
